import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePrimaryOrgId } from "@/hooks/usePrimaryOrgId";
import { useOrgSubscriptionState, type OrgSubscriptionState } from "@/hooks/useOrgSubscriptionState";

/**
 * Routes where the trial / lock banner must NEVER render.
 * Exported for unit testing alongside `isBannerHiddenPath`.
 */
export const BANNER_HIDDEN_PATHS = [
  "/",
  "/login",
  "/signup",
  "/auth/handoff",
  "/billing",
  "/finance/billing",
] as const;

/**
 * Pure, unit-safe matcher: returns true when the banner must be hidden
 * for the given pathname. Trailing slashes and query strings are
 * tolerated; matching is exact against the normalized pathname.
 */
export function isBannerHiddenPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  // Strip query/hash if a full path was passed in by mistake.
  const noQuery = pathname.split("?")[0].split("#")[0];
  // Normalize trailing slash (but keep root "/").
  const normalized =
    noQuery.length > 1 && noQuery.endsWith("/") ? noQuery.slice(0, -1) : noQuery;
  return (BANNER_HIDDEN_PATHS as readonly string[]).includes(normalized);
}

function lockedCopy(state: OrgSubscriptionState): string {
  if (state.status === "canceled") {
    return "Your subscription was canceled. Reactivate to restore access.";
  }
  if (state.status === "past_due") {
    return "Your subscription is past due. Reactivate to restore access.";
  }
  return "Your free trial has ended. Reactivate to restore access.";
}

export function TrialStatusBanner() {
  const location = useLocation();
  const { orgId } = usePrimaryOrgId();
  const { state } = useOrgSubscriptionState(orgId);

  if (isBannerHiddenPath(location.pathname)) return null;
  if (!state) return null;
  if (state.status === "active" && !state.is_locked) return null;

  const isSuperAdmin = state.is_org_super_admin;

  // 1. Locked
  if (state.is_locked) {
    return (
      <div className="sticky top-0 z-50 w-full bg-red-600 text-white px-4 py-2 text-sm flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 shrink-0" aria-hidden />
          <span>{lockedCopy(state)}</span>
        </div>
        {isSuperAdmin ? (
          <Button asChild size="sm" variant="secondary" className="bg-white text-red-700 hover:bg-white/90">
            <Link to="/billing">Reactivate now</Link>
          </Button>
        ) : (
          <span className="text-white/90">Ask your Super Admin to reactivate.</span>
        )}
      </div>
    );
  }

  // 2. Urgent trial
  if (state.is_trialing && state.trial_days_remaining <= 3) {
    const days = Math.max(state.trial_days_remaining, 0);
    return (
      <div className="sticky top-0 z-50 w-full bg-amber-500 text-white px-4 py-2 text-sm flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          <span>
            {days} day{days === 1 ? "" : "s"} left in your free trial
          </span>
        </div>
        {isSuperAdmin ? (
          <Button asChild size="sm" variant="secondary" className="bg-white text-amber-700 hover:bg-white/90">
            <Link to="/billing">Upgrade plan</Link>
          </Button>
        ) : (
          <span className="text-white/90">Ask your Super Admin to upgrade.</span>
        )}
      </div>
    );
  }

  // 3. Healthy trial
  if (state.is_trialing) {
    const days = state.trial_days_remaining;
    return (
      <div className="sticky top-0 z-50 w-full bg-[#0A3D7C] text-white px-4 py-2 text-sm flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
          <span>
            {days} day{days === 1 ? "" : "s"} left in your free trial
          </span>
        </div>
        {isSuperAdmin ? (
          <Button asChild size="sm" variant="secondary" className="bg-white text-[#0A3D7C] hover:bg-white/90">
            <Link to="/billing">Upgrade plan</Link>
          </Button>
        ) : (
          <span className="text-white/90">Ask your Super Admin to upgrade.</span>
        )}
      </div>
    );
  }

  return null;
}

export default TrialStatusBanner;